import { useState, useEffect } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import {
  AlertCircle, Loader, Gauge, Smartphone, Monitor, RefreshCw,
  Zap, Eye, Shield, Search, TrendingUp, Clock,  
  CheckCircle, AlertTriangle, XCircle
} from 'lucide-react';

const PageSpeed = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [strategy, setStrategy] = useState('mobile');
  const [customUrl, setCustomUrl] = useState('');
  const [testUrl, setTestUrl] = useState('');

  useEffect(() => {
    if (selectedSite && !testUrl) {
      setTestUrl(selectedSite.url);
      setCustomUrl(selectedSite.url);
    }
  }, [selectedSite]);

  const fetchPageSpeed = async (url = null, strat = null) => {
    if (!selectedSite) {
      setError('Please select a site');
      return;
    }

    const targetUrl = url || testUrl || selectedSite.url;
    const targetStrategy = strat || strategy;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/app/api/analytics/page-speed.php?siteId=${selectedSite.id}&url=${encodeURIComponent(targetUrl)}&strategy=${targetStrategy}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setData(result.data);
        setTestUrl(targetUrl);
      } else {
        setError(result.message || 'Failed to fetch PageSpeed data');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStrategyChange = (newStrategy) => {
    setStrategy(newStrategy);
    if (testUrl) {
      fetchPageSpeed(testUrl, newStrategy);
    }
  };

  const handleCustomUrlTest = (e) => {
    e.preventDefault();
    if (customUrl) {
      fetchPageSpeed(customUrl, strategy);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 90) return 'bg-green-100 border-green-300';
    if (score >= 50) return 'bg-yellow-100 border-yellow-300';
    return 'bg-red-100 border-red-300';
  };

  const getScoreRing = (score) => {
    if (score >= 90) return 'ring-green-500';
    if (score >= 50) return 'ring-yellow-500';
    return 'ring-red-500';
  };

  const getMetricStatus = (score) => {
    if (score >= 0.9) return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' };
    if (score >= 0.5) return { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50' };
    return { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' };
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb > 1024) {
      return `${(kb / 1024).toFixed(1)} MB`;
    }
    return `${kb.toFixed(0)} KB`;
  };

  const formatMs = (ms) => {
    if (!ms) return '';
    if (ms > 1000) {
      return `${(ms / 1000).toFixed(1)} s`;
    }
    return `${ms.toFixed(0)} ms`;
  };

  if (!selectedSite) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-yellow-50 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600" />
          <p className="text-yellow-800">Please select a site to analyze</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Gauge className="h-8 w-8 text-blue-600" />
            Page Speed & Core Web Vitals
          </h1>
          <p className="text-gray-600 mt-2">Analyze page performance using Google PageSpeed Insights</p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleCustomUrlTest} className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">URL to Test</label>
                <input
                  type="url"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  placeholder="https://example.com/page"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Device</label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => handleStrategyChange('mobile')}
                    className={`flex items-center gap-2 px-4 py-2 ${
                      strategy === 'mobile'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Smartphone className="h-4 w-4" />
                    Mobile
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStrategyChange('desktop')}
                    className={`flex items-center gap-2 px-4 py-2 border-l ${
                      strategy === 'desktop'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Monitor className="h-4 w-4" />
                    Desktop
                  </button>
                </div>
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4" />
                      Analyze
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Running PageSpeed analysis...</p>
              <p className="text-sm text-gray-500 mt-1">This may take up to 60 seconds</p>
            </div>
          </div>
        )}

        {/* Results */}
        {data && !loading && (
          <div className="space-y-6">
            {/* Tested URL */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium">Tested:</span>
                  <a href={data.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {data.url}
                  </a>
                  <span className="px-2 py-0.5 bg-gray-100 rounded text-xs uppercase">
                    {data.strategy}
                  </span>
                </div>
                <button
                  onClick={() => fetchPageSpeed()}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                >
                  <RefreshCw className="h-4 w-4" />
                  Re-test
                </button>
              </div>
            </div>

            {/* Score Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(data.scores).map(([key, score]) => (
                <div key={key} className={`bg-white rounded-lg shadow p-6 border-2 ${getScoreBg(score.score)}`}>
                  <div className="flex items-center justify-between mb-2">
                    {key === 'performance' && <Zap className={`h-5 w-5 ${getScoreColor(score.score)}`} />}
                    {key === 'accessibility' && <Eye className={`h-5 w-5 ${getScoreColor(score.score)}`} />}
                    {key === 'best_practices' && <Shield className={`h-5 w-5 ${getScoreColor(score.score)}`} />}
                    {key === 'seo' && <Search className={`h-5 w-5 ${getScoreColor(score.score)}`} />}
                  </div>
                  <div className={`text-4xl font-bold ${getScoreColor(score.score)}`}>
                    {score.score ?? 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">{score.title}</div>
                </div>
              ))}
            </div>

            {/* Core Web Vitals */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                Core Web Vitals
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(data.coreWebVitals).map(([key, metric]) => {
                  const status = getMetricStatus(metric.score);
                  const StatusIcon = status.icon;
                  return (
                    <div key={key} className={`p-4 rounded-lg border ${status.bg}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{metric.title}</div>
                          <div className={`text-2xl font-bold mt-1 ${status.color}`}>
                            {metric.value}
                          </div>
                        </div>
                        <StatusIcon className={`h-5 w-5 ${status.color}`} />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">{metric.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Opportunities */}
            {data.opportunities && data.opportunities.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-orange-600" />
                  Opportunities
                  <span className="text-sm font-normal text-gray-500">- Potential savings</span>
                </h3>
                <div className="space-y-3">
                  {data.opportunities.map((opp) => (
                    <div key={opp.id} className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{opp.title}</div>
                          {opp.displayValue && (
                            <div className="text-sm text-orange-700 mt-1">{opp.displayValue}</div>
                          )}
                        </div>
                        {opp.savings && (
                          <div className="text-right text-sm">
                            {opp.savings.ms && (
                              <div className="text-orange-600 font-medium">
                                Save {formatMs(opp.savings.ms)}
                              </div>
                            )}
                            {opp.savings.bytes && (
                              <div className="text-orange-600">
                                {formatBytes(opp.savings.bytes)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Diagnostics */}
            {data.diagnostics && data.diagnostics.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  Diagnostics
                </h3>
                <div className="space-y-2">
                  {data.diagnostics.map((diag) => {
                    const status = getMetricStatus(diag.score);
                    const StatusIcon = status.icon;
                    return (
                      <div key={diag.id} className="p-3 bg-gray-50 rounded-lg flex items-center gap-3">
                        <StatusIcon className={`h-5 w-5 flex-shrink-0 ${status.color}`} />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{diag.title}</div>
                          {diag.displayValue && (
                            <div className="text-xs text-gray-500">{diag.displayValue}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Info */}
            <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">About this data</p>
                  <p>Results are from Google PageSpeed Insights API using Lighthouse. Scores of 90+ are good, 50-89 need improvement, and below 50 are poor. Core Web Vitals are key metrics that affect user experience and SEO rankings.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Initial State */}
        {!data && !loading && !error && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Gauge className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Analyze</h3>
            <p className="text-gray-500 mb-6">Enter a URL and click Analyze to check page speed and Core Web Vitals</p>
            <button
              onClick={() => fetchPageSpeed()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Zap className="h-5 w-5" />
              Analyze Homepage
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PageSpeed;
